/**
 * @file index.tsx
 *
 * @fileoverview Exports job wizard hooks.
 */

import { useContext, useEffect, useState } from 'react';
import { getBundleFromQueryParams } from '~/jobiqo/components/Job/Wizard/utils';
import { PathContext } from '~/jobiqo/context/app';
import { PathContextInterface } from '~/jobiqo/models/app';
import { Product } from '~/jobiqo/types/types';
import { useQuery } from '@apollo/client';
import GET_DEFAULT_PRODUCT from '~/jobiqo/pages/job/wizard/bundles/graphql/queries/getDefaultProduct.gql';

export function useBundleIdParam(jobId?: number) {
  const [bundleId, setBundleId] = useState<number | null>(null);
  const { path }: PathContextInterface = useContext(PathContext);

  useEffect(() => {
    // 1. Extract bundle id from query param.
    setBundleId(getBundleFromQueryParams(path) ?? null);
  }, [path]);

  return bundleId;
}

/**
 * Fetches the default product bundle.
 * @param currentBundleExists
 *  If there is current bundle in the query.
 * @param locale
 *  The current locale.
 *
 * @returns defaultProductBundle
 *  In case bundle is not defined in the URL query params and bundle is set as default product return it.
 */
export function useDefaultProductBundle(
  currentBundleExists: boolean,
  locale?: string,
): Product | null {
  let defaultProductBundle: Product | null = null;

  const { error: defaultProductError, data: defaultProductData } = useQuery(
    GET_DEFAULT_PRODUCT,
    {
      variables: {
        language: locale,
      },
      skip: currentBundleExists,
    },
  );

  // If there is no current bundle in the query and default product type of job bundle return it.
  if (
    !currentBundleExists &&
    !defaultProductError &&
    defaultProductData?.getDefaultProduct?.type === 'job_bundle'
  ) {
    defaultProductBundle = defaultProductData.getDefaultProduct;
  }

  return defaultProductBundle;
}
